import React, { useEffect, useState } from 'react';
import { analytics, trips as tripsApi } from '../services/api';
import { TripStatusBadge } from '../components/ui/StatusBadge';
import { CardSkeleton } from '../components/ui/LoadingSpinner';
import { useAuth } from '../store/AuthContext';

function KPICard({ icon, label, value, delta, deltaPositive, color = 'blue', loading }) {
  const colorMap = {
    blue:   'from-blue-500 to-blue-600',
    green:  'from-green-500 to-green-600',
    orange: 'from-orange-400 to-orange-500',
    purple: 'from-purple-500 to-purple-600',
    teal:   'from-teal-500 to-teal-600',
    red:    'from-red-500 to-red-600',
  };
  if (loading) return <CardSkeleton />;
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${colorMap[color]} flex items-center justify-center text-white text-xl flex-shrink-0 shadow-sm`}>
          {icon}
        </div>
        {delta && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${deltaPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {delta}
          </span>
        )}
      </div>
      <div className="mt-3">
        <div className="text-3xl font-bold text-gray-900 tracking-tight">{value}</div>
        <div className="text-sm text-gray-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function ActivityItem({ trip }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 px-4 -mx-4 rounded-lg transition-colors cursor-pointer">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
        trip.status === 'in_progress' ? 'bg-green-500 animate-pulse' :
        trip.status === 'dispatched'  ? 'bg-yellow-500 animate-pulse' :
        trip.status === 'completed'   ? 'bg-teal-500' :
        trip.status === 'cancelled'   ? 'bg-red-500' : 'bg-gray-400'
      }`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-gray-900">{trip.trip_number}</span>
          <TripStatusBadge status={trip.status} size="xs" />
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5">
          {trip.origin_address?.split(',')[0]} → {trip.destination_address?.split(',')[0]}
        </p>
      </div>
      <div className="text-xs text-gray-400 flex-shrink-0">
        {trip.driver_name || '—'}
      </div>
    </div>
  );
}

export function Dashboard({ onNavigate }) {
  const { user } = useAuth();
  const [kpis, setKpis]         = useState(null);
  const [recentTrips, setRecent] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      analytics.kpis('30d'),
      tripsApi.list({ limit: 10 }),
    ])
      .then(([k, t]) => {
        setKpis(k);
        setRecent(Array.isArray(t) ? t.slice(0, 8) : (t?.data || []).slice(0, 8));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Welcome back, {user?.first_name} 👋
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Here's what's happening with your fleet today.
            </p>
          </div>
          <button onClick={() => onNavigate('trips')} className="btn-primary">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Trip
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <KPICard icon="🚛" label="Total Trips (30d)"  value={kpis?.total_trips ?? '—'}  color="blue"   loading={loading} />
          <KPICard icon="✅" label="Completed"          value={kpis?.completed ?? '—'}    color="green"  loading={loading} delta="+12%" deltaPositive />
          <KPICard icon="🔴" label="Active Now"         value={kpis?.active ?? '—'}       color="orange" loading={loading} />
          <KPICard icon="📏" label="Miles Driven"       value={kpis ? `${Math.round(kpis.total_miles).toLocaleString()} mi` : '—'} color="purple" loading={loading} />
          <KPICard icon="⛽" label="Avg Fleet MPG"      value={kpis?.avg_mpg > 0 ? `${kpis.avg_mpg} mpg` : '—'} color="teal" loading={loading} />
          <KPICard icon="👥" label="Active Drivers"     value={kpis?.active_drivers ?? '—'} color="blue" loading={loading} />
          <KPICard icon="🚐" label="Fleet Vehicles"     value={kpis?.fleet_vehicles ?? '—'} color="purple" loading={loading} />
          <KPICard icon="⏱️" label="On-Time Rate"       value={kpis ? `${kpis.on_time_pct}%` : '—'} color="green" loading={loading} />
        </div>

        {/* Quick Actions + Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              {[
                { label: 'Create New Trip',       icon: '🗺️', page: 'trips',    color: 'hover:bg-blue-50 hover:border-blue-200' },
                { label: 'View All Drivers',      icon: '👤', page: 'drivers',  color: 'hover:bg-green-50 hover:border-green-200' },
                { label: 'Fleet Vehicles',        icon: '🚛', page: 'vehicles', color: 'hover:bg-purple-50 hover:border-purple-200' },
                { label: 'HOS Compliance',        icon: '⏱️', page: 'hos',      color: 'hover:bg-orange-50 hover:border-orange-200' },
                { label: 'Fleet Analytics',       icon: '📊', page: 'analytics',color: 'hover:bg-teal-50 hover:border-teal-200' },
              ].map(a => (
                <button
                  key={a.page}
                  onClick={() => onNavigate(a.page)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border border-transparent text-sm font-medium text-gray-700 transition-colors ${a.color}`}
                >
                  <span className="text-lg">{a.icon}</span>
                  {a.label}
                  <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Trips */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="font-semibold text-gray-900">Recent Trips</h3>
              <button onClick={() => onNavigate('trips')} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                View all →
              </button>
            </div>
            <div className="p-4">
              {loading ? (
                <div className="space-y-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : recentTrips.length > 0 ? (
                recentTrips.map(trip => <ActivityItem key={trip.id} trip={trip} />)
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">No trips yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Distribution */}
        {kpis && (
          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Trip Status Overview (Last 30 Days)</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
              {[
                { label: 'Draft',       value: kpis.total_trips - kpis.completed - kpis.active - kpis.cancelled, color: 'bg-gray-400' },
                { label: 'Active',      value: kpis.active,     color: 'bg-green-500' },
                { label: 'Completed',   value: kpis.completed,  color: 'bg-teal-500' },
                { label: 'Pending',     value: kpis.pending,    color: 'bg-blue-500' },
                { label: 'Cancelled',   value: kpis.cancelled,  color: 'bg-red-500' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className={`w-3 h-3 rounded-full ${s.color} mx-auto mb-2`} />
                  <div className="text-2xl font-bold text-gray-900">{Math.max(0, s.value || 0)}</div>
                  <div className="text-xs text-gray-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
